import { z } from "zod/v4";
import type { AthleteRemovalConfirmation } from "../schemas/athlete";
import {
	athleteDuplicatesSchema,
	claimRequestSchema,
	mergeAthleteDuplicatesResultSchema,
	scrapedAthleteSchema,
	syncResultSchema,
} from "../schemas/athlete";
import type { ListParams } from "./games";
import { fetchApi, fetchFormData, fetchVoid, paginatedSchema, toSearchParams } from "./internal";

import { removals } from "./removals";

const ATHLETE_IMAGE_FIELD = "image";

/** Sort fields `GET /admin/athletes` whitelists; anything else is rejected server-side. */
export const ATHLETE_SORT_FIELDS = [
	"club",
	"created_at",
	"federation",
	"first_name",
	"gender",
	"last_name",
	"last_scraped_at",
	"source",
] as const;

export type AthleteSortField = (typeof ATHLETE_SORT_FIELDS)[number];

/** Narrows a free-form table sort column to one the server accepts. */
export const isAthleteSortField = (value: string | undefined): value is AthleteSortField =>
	value !== undefined && (ATHLETE_SORT_FIELDS as readonly string[]).includes(value);

export type AthletesListParams = ListParams & {
	claimed_by?: string;
	federation?: string;
	gender?: "male" | "female";
	has_image?: boolean;
	include_hidden?: boolean;
};

export type ClaimRequestsListParams = ListParams & { status?: string; user_id?: string };

export const athletes = {
	claims: {
		approve: (id: string, sendEmail = true) =>
			fetchVoid(`/admin/athletes/claims/${id}/approve`, {
				body: JSON.stringify({ send_email: sendEmail }),
				method: "POST",
			}),
		create: (athleteId: string, body: { user_id: string; send_email: boolean }) =>
			fetchApi(`/admin/athletes/${athleteId}/claims`, claimRequestSchema, {
				body: JSON.stringify(body),
				method: "POST",
			}),
		list: ({ limit = 20, offset = 0, status, user_id }: ClaimRequestsListParams = {}) => {
			const qs = toSearchParams({ limit, offset, status, user_id });
			return fetchApi(
				`/admin/athletes/claims${qs ? `?${qs}` : ""}`,
				paginatedSchema(claimRequestSchema),
			);
		},
		reject: (id: string) =>
			fetchVoid(`/admin/athletes/claims/${id}/reject`, {
				method: "POST",
			}),
		revoke: (id: string) =>
			fetchVoid(`/admin/athletes/claims/${id}/revoke`, {
				method: "POST",
			}),
		/** Queues the approval email of an approved claim again. */
		sendEmail: (id: string) =>
			fetchVoid(`/admin/athletes/claims/${id}/emails`, {
				method: "POST",
			}),
	},
	createImage: (id: string, body: { instagram_handle?: string } = {}) =>
		fetchApi(`/admin/athletes/${id}/images`, scrapedAthleteSchema, {
			body: JSON.stringify(body),
			method: "POST",
		}),
	duplicates: () => fetchApi("/admin/athletes/duplicates", athleteDuplicatesSchema),
	enrichImages: () =>
		fetchApi(
			"/admin/athletes/images",
			z.object({ message: z.string().optional(), pending: z.number() }),
			{ method: "POST" },
		),
	get: (id: string) => fetchApi(`/admin/athletes/${id}`, scrapedAthleteSchema),
	list: (params: AthletesListParams = {}) => {
		const qs = toSearchParams(params);
		return fetchApi(`/admin/athletes${qs ? `?${qs}` : ""}`, paginatedSchema(scrapedAthleteSchema));
	},
	mergeDuplicates: () =>
		fetchApi("/admin/athletes/duplicates/merge", mergeAthleteDuplicatesResultSchema, {
			method: "POST",
		}),
	migrateImages: () =>
		fetchApi(
			"/admin/athletes/images/migrations",
			z.object({ message: z.string().optional(), pending: z.number() }),
			{ method: "POST" },
		),
	removals,
	replaceImage: (id: string, file: File) => {
		const formData = new FormData();
		formData.append(ATHLETE_IMAGE_FIELD, file);
		return fetchFormData(`/admin/athletes/${id}/image`, scrapedAthleteSchema, formData, "PUT");
	},
	sendRemovalConfirmation: (id: string, body: AthleteRemovalConfirmation): Promise<void> =>
		fetchVoid(`/admin/athletes/${id}/removal-confirmations`, {
			body: JSON.stringify(body),
			method: "POST",
		}),
	sync: () =>
		fetchApi("/admin/athletes/sync", syncResultSchema, {
			method: "POST",
		}),
};
